import React, { useEffect, useState } from 'react';
import useClient from 'api/client';
import Header, { ButtonConfig } from 'components/header/Header';
import StatusWithTooltip from 'components/StatusWithTooltip/StatusWithTooltip';
import { useModal } from 'hooks/useModal';
import useDatasetCredentials from '../hooks/useDatasetCredentials';
import StackDetails, { StackType } from 'views/Stack/StackDetails';
import FolderList from './FolderList';
import SchemaViewer from './SchemaViewer';
import PermissionList from './Permissions';
import Uploader from './Uploader';
import TableList from './TableList';
import { NDivider, NStatusTag } from '@innersource-nn/novoui-react';
import { trackEvent } from 'analytics/AWSRumService';
import EditDataset from './Modals/EditDatasetModal';
import useDatasetDetails from '../hooks/useDatasetDetails';
import EditorDetails from 'components/details/EditorDetails';
import Spinner from 'components/loader/Spinner';
import { TabsPanel } from 'components/tabs/TabsPanel';
import { useLocation } from 'react-router-dom';
import {
    ClassificationProps,
    getClassificationProps,
} from './DatasetClassification';
import { DataClassificationsOptions, UserRoleForDataset } from 'types/Dataset';

interface SuccessDatasetClassificationProps {
    isUserAdmin: boolean;
    status: string;
}

const DatasetContent = ({
    isUserAdmin,
    status,
}: SuccessDatasetClassificationProps) => {
    const client = useClient();
    const location = useLocation();

    const { items, dataset, error, loading, fetchDetails } =
        useDatasetDetails();

    const [editModalOpen, setEditModalOpen] = useState(false);

    // CUSTOM HOOKS:
    const {
        openDatasetAccess,
        openDeleteDataset,
        openCredentialsDataset,
        openAWSResourcesModal,
    } = useModal();

    const { credentials, credentialError, credentialLoading, copyCredentials } =
        useDatasetCredentials(isUserAdmin);

    useEffect(() => {
        if (location.search === '?tab=schema') {
            trackEvent('tab_click_dataset_schema_viewer', {
                location: 'dataset',
            });
        }
    }, [location.search]);

    const [clasificationProps, setClassificationProps] =
        useState<ClassificationProps>({
            icon: 'success',
            state: 'info',
            label: DataClassificationsOptions.InternalUse,
        });

    const tooltipContent = !isUserAdmin
        ? 'To access this functionality you need to be part of the admin group the dataset belongs to.'
        : !dataset.enableCrawler
          ? 'Crawler needs to be enabled for this functionality. Click on the edit Button on the top right corner to enable the crawler'
          : '';

    const buttonConfigs: ButtonConfig[] = [
        {
            label: 'Request access',
            onClick: () =>
                openDatasetAccess({
                    resourceUri: dataset.datasetUri,
                    datasetName: dataset.label,
                }),
            icon: 'key',
        },
        {
            label: 'AWS Links',
            onClick: () =>
                openAWSResourcesModal({
                    datasetUri: dataset.datasetUri,
                    environmentUri: dataset.environment.environmentUri,
                    title: 'AWS Links',
                }),
            icon: 'external',
            tooltip: tooltipContent,
        },
        {
            label: 'Credentials',
            onClick: () =>
                openCredentialsDataset({
                    credentials,
                    loading: credentialLoading,
                    copyCredentials,
                }),
            icon: 'doselog',
            disabled: !isUserAdmin || credentialError !== '',
            tooltip: credentialError === '' ? tooltipContent : credentialError,
        },
        {
            label: 'Edit',
            onClick: () => setEditModalOpen(true),
            icon: 'edit',
            tooltip: tooltipContent,
        },
        {
            label: 'Remove',
            onClick: () =>
                openDeleteDataset({
                    label: dataset.label,
                    datasetUri: dataset.datasetUri,
                    terminationProtection: dataset.stack.terminationProtection,
                }),
            red: true,
            icon: 'trash',
            tooltip: tooltipContent,
        },
    ];

    useEffect(() => {
        const props: ClassificationProps = getClassificationProps(
            dataset.confidentiality,
        );
        setClassificationProps(props);
    }, [dataset.confidentiality]);

    return loading ? (
        <Spinner />
    ) : (
        <div>
            <EditDataset
                dataset={dataset}
                visible={editModalOpen}
                onClose={() => {
                    fetchDetails();
                    setEditModalOpen(false);
                }}
            />
            <Header
                title={dataset.label}
                buttonConfigs={
                    isUserAdmin
                        ? buttonConfigs
                        : buttonConfigs.filter(
                              (config) => config.label === 'Request access',
                          )
                }
                backButton
            />
            <div className="flex gap-8 items-center">
                <div className="text-true-blue text-sm">
                    Created: {dataset.created}
                </div>
                <StatusWithTooltip content={status} />
                <NDivider
                    orientation="vertical"
                    color="graniteGrey200"
                    className="h-auto"
                />
                <NStatusTag
                    label={dataset.visibility}
                    variant="rounded"
                    icon={
                        dataset.visibility === 'Private'
                            ? 'visibilityOff'
                            : 'visibilityOn'
                    }
                />
                <NStatusTag
                    variant="rounded"
                    label={clasificationProps.label}
                    icon={clasificationProps.icon}
                    state={clasificationProps.state}
                />
                {UserRoleForDataset.NoPermission ===
                dataset.userRoleForDataset ? (
                    <NStatusTag
                        label="No Permission"
                        variant="rounded"
                        icon="lock"
                        state="alert"
                    />
                ) : (
                    <NStatusTag
                        label={dataset.userRoleForDataset}
                        variant="rounded"
                        icon="person"
                    />
                )}
            </div>
            <TabsPanel
                content={[
                    {
                        id: 'overview',
                        label: 'Overview',
                        content: (
                            <EditorDetails
                                expandable={true}
                                explandableTitle="Dataset Details"
                                error={error}
                                items={items}
                                type="dataset"
                            />
                        ),
                    },
                    {
                        id: 'schema',
                        label: 'Schema ',
                        disabled: !isUserAdmin,
                        tooltip: tooltipContent,
                        content: <SchemaViewer />,
                    },
                    {
                        id: 'tables',
                        label: 'Tables',
                        disabled: !isUserAdmin,
                        tooltip: tooltipContent,
                        content: (
                            <TableList
                                enableCrawler={dataset.enableCrawler ?? true}
                                datasetLabel={dataset.label}
                            />
                        ),
                    },
                    {
                        id: 'folders',
                        label: 'Folders ',
                        content: <FolderList isUserAdmin={isUserAdmin} />,
                    },
                    {
                        id: 'permissions',
                        label: 'Permissions ',
                        disabled:
                            UserRoleForDataset.NoPermission ===
                            dataset.userRoleForDataset,
                        content: <PermissionList />,
                        tooltip:
                            'To access this functionality you need to be part of the admin group the dataset belongs to, or have access to this dataset. You can do that by clicking the request access button on the upper right corner.',
                    },
                    {
                        id: 'upload',
                        label: 'Upload',
                        content: <Uploader dataset={dataset} client={client} />,
                        disabled: !isUserAdmin || !dataset.enableCrawler,
                        tooltip: tooltipContent,
                    },
                    {
                        id: 'stack',
                        label: 'Stack ',
                        content: <StackDetails type={StackType.dataset} />,
                        disabled: !isUserAdmin,
                        tooltip: tooltipContent,
                    },
                ]}
            />
        </div>
    );
};

export default DatasetContent;
